import { ref } from "vue";

import { ApiError } from "../api/client";
import { OFFLINE_MESSAGE } from "../api/messages";

/**
 * One request, with the two things every screen has to show about it: whether
 * it is running, and what went wrong.
 *
 * Every action in the admin goes through this, so "Ladezustand, Erfolg,
 * Fehler" is not something each screen has to remember to do.
 */
export function useAsyncAction<Args extends unknown[], Result>(
  action: (...args: Args) => Promise<Result>,
) {
  const busy = ref(false);
  const errors = ref<string[]>([]);

  function clear(): void {
    errors.value = [];
  }

  /**
   * Runs the action. Returns undefined when it failed - the messages are in
   * `errors` then, already in German.
   */
  async function run(...args: Args): Promise<Result | undefined> {
    busy.value = true;
    errors.value = [];

    try {
      return await action(...args);
    } catch (error) {
      errors.value =
        error instanceof ApiError
          ? error.messages
          : [error instanceof Error ? error.message : OFFLINE_MESSAGE];
      return undefined;
    } finally {
      busy.value = false;
    }
  }

  return { busy, errors, run, clear };
}
